import type { JournalEntry, User } from "./types";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && init.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error ?? `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  publicJournal: () => request<{ entries: JournalEntry[] }>("/api/public/journal", { cache: "no-store" }),
  me: () => request<{ user: User }>("/api/me"),
  login: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  forgotPassword: (email: string) =>
    request<{ accepted: boolean; developmentResetUrl?: string }>("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    request<{ changed: true }>("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) }),
  journal: () => request<{ entries: JournalEntry[] }>("/api/journal"),
  trash: () => request<{ entries: JournalEntry[] }>("/api/journal/trash"),
  createJournalEntry: (entry: Pick<JournalEntry, "title" | "body" | "isPublic" | "tags">) =>
    request<{ entry: JournalEntry }>("/api/journal", { method: "POST", body: JSON.stringify(entry) }),
  updateJournalEntry: (id: string, entry: Pick<JournalEntry, "title" | "body" | "isPublic" | "tags">) =>
    request<{ entry: JournalEntry }>(`/api/journal/${id}`, { method: "PUT", body: JSON.stringify(entry) }),
  deleteJournalEntry: (id: string) => request<void>(`/api/journal/${id}`, { method: "DELETE" }),
  restoreJournalEntry: (id: string) => request<{ entry: JournalEntry }>(`/api/journal/${id}/restore`, { method: "POST" }),
  permanentlyDeleteJournalEntry: (id: string) => request<void>(`/api/journal/${id}/permanent`, { method: "DELETE" }),
  exportJournal: async (filter: { from?: string; to?: string; visibility: "all" | "public" | "private" }) => {
    const params = new URLSearchParams();
    if (filter.from) params.set("from", filter.from);
    if (filter.to) params.set("to", filter.to);
    params.set("visibility", filter.visibility);
    const response = await fetch(`/api/journal/export?${params}`, { credentials: "same-origin" });
    if (!response.ok) throw new Error("export_failed");
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? "dnevnik-petr-osetr.docx";
    return { blob: await response.blob(), filename };
  },
};
